import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import { getAppwriteStorageUrl } from '../../utils/helpers';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { signInWithGoogle, signInWithLinkedIn } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Ambient glow canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create floating color orbs with MUCH brighter colors
    interface Orb {
      x: number;
      y: number;
      radius: number;
      dx: number;
      dy: number;
      color: string;
    }

    const orbs: Orb[] = [
      { x: canvas.width * 0.2, y: canvas.height * 0.3, radius: 300, dx: 0.4, dy: 0.3, color: 'rgba(82, 96, 255, 0.8)' },
      { x: canvas.width * 0.8, y: canvas.height * 0.5, radius: 350, dx: -0.3, dy: 0.4, color: 'rgba(138, 43, 226, 0.7)' },
      { x: canvas.width * 0.5, y: canvas.height * 0.7, radius: 280, dx: 0.35, dy: -0.35, color: 'rgba(0, 191, 255, 0.75)' },
      { x: canvas.width * 0.6, y: canvas.height * 0.2, radius: 250, dx: -0.25, dy: 0.3, color: 'rgba(255, 0, 128, 0.6)' },
      { x: canvas.width * 0.3, y: canvas.height * 0.8, radius: 320, dx: 0.3, dy: -0.25, color: 'rgba(30, 50, 180, 0.65)' },
    ];

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      orbs.forEach((orb) => {
        // Update position
        orb.x += orb.dx;
        orb.y += orb.dy;

        // Bounce off edges
        if (orb.x + orb.radius > canvas.width || orb.x - orb.radius < 0) {
          orb.dx = -orb.dx;
        }
        if (orb.y + orb.radius > canvas.height || orb.y - orb.radius < 0) {
          orb.dy = -orb.dy;
        }

        // Draw gradient orb with stronger colors
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        gradient.addColorStop(0, orb.color);
        gradient.addColorStop(0.5, orb.color.replace(/[\d.]+\)$/, '0.3)'));
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Magic Link Sign In (Appwrite)
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setMagicLinkSent(false);

    try {
      // Send magic link to user's email
      await backendProvider.auth.sendMagicLink(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link');
      setMagicLinkSent(false);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);

    try {
      // CRITICAL FIX: Clear Google OAuth state before initiating new login
      console.log('🧹 Step 0/3: Clearing Google OAuth state in localStorage...');
      localStorage.removeItem('oauth_provider');
      localStorage.removeItem('oauth_state');
      localStorage.removeItem('google_auth_token');
      localStorage.removeItem('gapi.auth.picker');
      localStorage.removeItem('google_cached_login');
      console.log('✅ Google OAuth state cleared');

      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithLinkedIn();
    } catch (err: any) {
      setError(err.message || 'LinkedIn sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 sm:p-6 md:p-8 relative overflow-hidden">
      {/* Animated Canvas Background with Blur */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 0, filter: 'blur(120px)' }}
      />

      {/* Radial Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background: 'radial-gradient(circle, transparent 0%, rgba(5, 5, 5, 0.3) 70%, rgba(5, 5, 5, 0.7) 100%)'
        }}
      ></div>

      {/* Logo - Top Left - Hidden on mobile, visible on tablet+ */}
      <img
        src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e3f8003c17bb7fc9/files/6940e6360012a5ff6f2a/download?project=693c0579001e8b06c6d6"
        alt="Brainiacs Logo"
        className="hidden md:block fixed top-6 left-6 lg:left-16 w-44 lg:w-72 h-auto z-50"
        crossOrigin="anonymous"
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo for Mobile - Centered above card */}
        <div className="md:hidden flex justify-center mb-8">
          <img
            src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e3f8003c17bb7fc9/files/6940e6360012a5ff6f2a/download?project=693c0579001e8b06c6d6"
            alt="Brainiacs Logo"
            className="w-44 h-auto"
            crossOrigin="anonymous"
          />
        </div>

        {/* Card with Glass Panel Effect */}
        <div className="glass-panel rounded-3xl p-6 sm:p-8 backdrop-blur-xl">
          {/* Title */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Welcome
            </h2>
            <p className="text-sm text-gray-400">
              Sign in to access your Brainiacs OS
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-500 text-sm">
              {error}
            </div>
          )}

          {/* Google Button */}
          <button
            onClick={handleGoogle}
            disabled={loading}
            className="w-full px-4 py-3 mb-4 bg-white text-black rounded-xl text-[15px] font-semibold cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-100 transition-colors flex items-center justify-center gap-3"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" className="flex-shrink-0">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{loading ? 'Signing in...' : 'Continue with Google'}</span>
          </button>

          {/* LinkedIn Button */}
          <button
            onClick={handleLinkedIn}
            disabled={loading}
            className="w-full px-4 py-3 mb-6 bg-[#0A66C2] text-white rounded-xl text-[15px] font-semibold cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#004182] transition-colors flex items-center justify-center gap-3"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white" className="flex-shrink-0">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            <span>{loading ? 'Signing in...' : 'Continue with LinkedIn'}</span>
          </button>

          {/* Divider */}
          <div className="relative mb-6 text-center">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10"></div>
            <span className="relative bg-[#0a0a0a] px-3 text-xs text-gray-500 uppercase">
              Or
            </span>
          </div>

          {/* Magic Link Form (Appwrite) */}
          {!magicLinkSent ? (
            <form onSubmit={handleMagicLink} autoComplete="on">
              <div className="mb-4">
                <label htmlFor="magic-link-email" className="block text-sm text-gray-300 mb-2 font-medium">
                  Email Address
                </label>
                <input
                  id="magic-link-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-xl text-white text-[15px] outline-none focus:border-[#5260FF]/50 transition-colors placeholder-gray-600 disabled:opacity-50"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-3 bg-[#5260FF] text-white rounded-xl text-[15px] font-semibold cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#4250EF] transition-colors shadow-lg shadow-[#5260FF]/20"
              >
                {loading ? 'Sending Magic Link...' : 'Send Magic Link'}
              </button>
            </form>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="text-sm font-semibold text-green-400">Magic Link Sent!</h3>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                We've sent a magic link to <span className="text-white font-medium">{email}</span>
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Click the link in your email to sign in. The link will expire in 15 minutes.
              </p>
              <button
                onClick={() => {
                  setMagicLinkSent(false);
                  setEmail('');
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Try a different email
              </button>
            </div>
          )}

          {/* Terms & Services */}
          <p className="mt-6 text-center text-xs text-gray-500">
            By registering you accept our{' '}
            <a
              href="https://brainiacs.wtf/terms-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#5260FF] hover:text-[#4250EF] transition-colors underline"
            >
              Terms & Services
            </a>
          </p>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-600">
          Secure authentication powered by Brainiacs OÜ
        </p>
      </div>
    </div>
  );
};
